import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Badge,

  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  FaUser,
  FaClipboardList,
  FaClock,
  FaCheckCircle,
  FaEuroSign,
  FaBell,
  FaEnvelope,
  FaChartLine,
  FaSearch,
  FaEdit,
  FaEye,
  FaCalendarAlt,
  FaMapMarkerAlt,

} from 'react-icons/fa';
import {
  selectFilteredMissions,
  selectRecentNotifications,
  selectRecentMessages,
  setMissionFilter,
  markNotificationAsRead,
  markMessageAsRead,
  fetchDashboardData
} from '../redux/slices/professionalDashboardSlice';
import { fetchUserProfile } from '../redux/slices/authSlice';
import authService from '../utils/authService';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import './DashboardPro.css';

// Composant StatsCard
const StatsCard = ({ icon: Icon, title, value, color, onClick }) => {
  return (
    <Card className="stats-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div" className="stats-value" style={{ color }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" className="stats-title">
              {title}
            </Typography>
          </Box>
          <Box className="stats-icon" style={{ backgroundColor: `${color}20` }}>
            <Icon style={{ color }} size={24} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Composant MissionCard
const MissionCard = ({ mission }) => {
  const navigate = useNavigate();
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'applied': return '#ff9800';
      case 'in_progress': return '#2196f3';
      case 'completed': return '#4caf50';
      default: return '#757575';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'applied': return 'Postulée';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminée';
      default: return status;
    }
  };
  
  return (
    <Card className="mission-card">
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box flex={1}>
            <Typography variant="h6" component="div" className="mission-title">
              {mission.title}
            </Typography>
            <Box display="flex" alignItems="center" mt={1} mb={1}>
              <Avatar src={mission.organizationLogo} sx={{ width: 24, height: 24, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {mission.organization}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={getStatusText(mission.status)}
            size="small"
            style={{ 
              backgroundColor: getStatusColor(mission.status),
              color: 'white'
            }}
          />
        </Box>
        
        <Box display="flex" alignItems="center" mb={1}>
          <FaCalendarAlt style={{ marginRight: 8, color: '#666' }} size={14} />
          <Typography variant="body2" color="text.secondary">
            {new Date(mission.startDate).toLocaleDateString('fr-FR')} - {new Date(mission.endDate).toLocaleDateString('fr-FR')}
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" mb={1}>
          <FaMapMarkerAlt style={{ marginRight: 8, color: '#666' }} size={14} />
          <Typography variant="body2" color="text.secondary">
            {mission.location}
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" mb={2}>
          <FaEuroSign style={{ marginRight: 8, color: '#4caf50' }} size={14} />
          <Typography variant="body2" style={{ color: '#4caf50', fontWeight: 'bold' }}>
            {mission.rate}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" className="mission-description">
          {mission.description}
        </Typography>
      </CardContent>
      
      <CardActions>
        <Button 
          size="small" 
          startIcon={<FaEye />}
          onClick={() => navigate(`/missions/${mission.id}`)}
        >
          Voir détails
        </Button>
      </CardActions>
    </Card>
  );
};

// Composant NotificationList
const NotificationList = ({ notifications, onMarkAsRead }) => {
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'mission_accepted': return <FaCheckCircle style={{ color: '#4caf50' }} />;
      case 'new_message': return <FaEnvelope style={{ color: '#2196f3' }} />;
      case 'payment_received': return <FaEuroSign style={{ color: '#4caf50' }} />;
      case 'mission_reminder': return <FaClock style={{ color: '#ff9800' }} />;
      case 'profile_view': return <FaUser style={{ color: '#9c27b0' }} />;
      default: return <FaBell style={{ color: '#757575' }} />;
    }
  };
  
  return (
    <List className="notification-list">
      {notifications.map((notification, index) => (
        <React.Fragment key={notification.id}>
          <ListItem 
            className={`notification-item ${!notification.read ? 'unread' : ''}`}
            onClick={() => onMarkAsRead(notification.id)}
          >
            <ListItemAvatar>
              {getNotificationIcon(notification.type)}
            </ListItemAvatar>
            <ListItemText
              primary={notification.title}
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(notification.date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              }
            />
            {!notification.read && (
              <Box className="unread-indicator" />
            )}
          </ListItem>
          {index < notifications.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

// Composant MessagePreview
const MessagePreview = ({ messages, onMarkAsRead }) => {
  const navigate = useNavigate();
  
  return (
    <List className="message-list">
      {messages.map((message, index) => (
        <React.Fragment key={message.id}>
          <ListItem 
            className={`message-item ${message.unread ? 'unread' : ''}`}
            onClick={() => {
              if (message.unread) onMarkAsRead(message.id);
              navigate('/messages');
            }}
          >
            <ListItemAvatar>
              <Avatar src={message.organizationLogo} />
            </ListItemAvatar>
            <ListItemText
              primary={message.organizationName}
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary" className="message-preview">
                    {message.lastMessage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(message.date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              }
            />
            {message.unread && (
              <Box className="unread-indicator" />
            )}
          </ListItem>
          {index < messages.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

// Composant RevenueChart (simple)
const RevenueChart = ({ revenueData }) => {
  const maxAmount = Math.max(...revenueData.monthlyData.map(d => d.amount));
  
  return (
    <Box className="revenue-chart">
      <Typography variant="h6" gutterBottom>
        Évolution des revenus
      </Typography>
      
      <Box className="chart-container">
        {revenueData.monthlyData.map((data, index) => (
          <Box key={index} className="chart-bar-container">
            <Box 
              className="chart-bar"
              style={{ 
                height: `${(data.amount / maxAmount) * 100}%`,
                backgroundColor: '#2196f3'
              }}
            />
            <Typography variant="caption" className="chart-label">
              {data.month.split(' ')[0]}
            </Typography>
            <Typography variant="caption" className="chart-value">
              {data.amount}€
            </Typography>
          </Box>
        ))}
      </Box>
      
      <Box className="revenue-summary" mt={2}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2">Total gagné:</Typography>
          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {revenueData.totalEarned}€
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2">Ce mois:</Typography>
          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
            {revenueData.currentMonth}€
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">En attente:</Typography>
          <Typography variant="body2" style={{ color: '#ff9800' }}>
            {revenueData.pendingPayments}€
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Composant principal DashboardPro
const DashboardPro = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Récupération des données de l'utilisateur connecté depuis authSlice
  const { user: authUser, isAuthenticated } = useSelector(state => state.auth);
  
  // Récupération des données du dashboard (missions, stats, etc.)
  const {
    stats,
    revenue,
    loading,
    error,
    unreadNotifications,
    unreadMessages
  } = useSelector(state => state.professionalDashboard);
  
  const filteredMissions = useSelector(selectFilteredMissions);
  const recentNotifications = useSelector(selectRecentNotifications);
  const recentMessages = useSelector(selectRecentMessages);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleResendEmail = async () => {
    try {
      await authService.resendEmailVerification();
      setSnackbar({
        open: true,
        message: 'Email de vérification renvoyé avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de l\'envoi de l\'email',
        severity: 'error'
      });
    }
  };

  // Fonction pour obtenir la profession de l'utilisateur
  const getUserProfession = (authUser) => {
    // Vérifier si specialties est un tableau
    if (authUser?.professional?.specialties) {
      if (Array.isArray(authUser.professional.specialties) && authUser.professional.specialties.length > 0) {
        return authUser.professional.specialties[0];
      }
      // Si specialties est une chaîne de caractères
      if (typeof authUser.professional.specialties === 'string' && authUser.professional.specialties.trim()) {
        return authUser.professional.specialties;
      }
    }
    
    // Vérifier les disciplines
    if (authUser?.professional?.disciplines) {
      if (Array.isArray(authUser.professional.disciplines) && authUser.professional.disciplines.length > 0) {
        return `Spécialiste ${authUser.professional.disciplines[0]}`;
      }
      // Si disciplines est une chaîne de caractères
      if (typeof authUser.professional.disciplines === 'string' && authUser.professional.disciplines.trim()) {
        const firstDiscipline = authUser.professional.disciplines.split(',')[0].trim();
        return `Spécialiste ${firstDiscipline}`;
      }
    }
    
    return 'Professionnel du Sport';
  };

  // Fonction pour obtenir la localisation de l'utilisateur
  const getUserLocation = (authUser) => {
    if (authUser?.address?.city) {
      const city = authUser.address.city;
      const country = authUser.address.country;
      return country && country !== 'France' ? `${city}, ${country}` : city;
    }
    if (authUser?.address?.location) {
      return authUser.address.location;
    }
    return 'France';
  };

  // Fonction pour obtenir l'avatar de l'utilisateur
  const getUserAvatar = (authUser) => {
    // Vérifier si l'utilisateur a un avatar uploadé
    if (authUser?.avatar?.url) {
      return authUser.avatar.url;
    }
    
    // Icône par défaut si pas d'avatar
    console.log('Utilisation de l\'avatar par défaut');
    return '/default-avatar.svg';
  };

  // Données utilisateur avec les vraies informations
  const user = authUser ? {
    id: authUser.id || authUser._id,
    name: authUser.fullName || `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim() || 'Utilisateur',
    email: authUser.email || '',
    avatar: getUserAvatar(authUser),
    profession: getUserProfession(authUser),
    location: getUserLocation(authUser),
    rating: authUser.professional?.rating?.average || 0,
    completedMissions: authUser.stats?.missionsCompleted || 0,
    experience: authUser.professional?.experience || 0,
    hourlyRate: authUser.professional?.hourlyRate || 0
  } : {
    id: 0,
    name: 'Utilisateur',
    email: '',
    avatar: '/default-avatar.svg',
    profession: 'Professionnel du Sport',
    location: 'France',
    rating: 0,
    completedMissions: 0,
    experience: 0,
    hourlyRate: 0
  };
  
  const [missionTab, setMissionTab] = useState(0);
  
  // Récupération de toutes les données du tableau de bord au chargement
  useEffect(() => {
    if (isAuthenticated && authUser?.id) {
      dispatch(fetchUserProfile()); // Récupérer les données utilisateur actualisées
      dispatch(fetchDashboardData());
    }
  }, [dispatch, isAuthenticated, authUser?.id]);


  
  const handleMissionFilterChange = (event, newValue) => {
    setMissionTab(newValue);
    const filters = ['all', 'applied', 'in_progress', 'completed'];
    dispatch(setMissionFilter(filters[newValue]));
  };
  
  const handleNotificationRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId));
  };
  
  const handleMessageRead = (messageId) => {
    dispatch(markMessageAsRead(messageId));
  };
  
  if (authUser && authUser.isEmailVerified === false) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: 'warning.main' }}>
            <FaEnvelope size={32} />
          </Avatar>
          <Typography variant="h5" gutterBottom>
            Vérification requise
          </Typography>
          <Typography color="text.secondary" paragraph>
            Votre compte a été créé avec succès, mais vous devez vérifier votre adresse email avant d'accéder au tableau de bord.
          </Typography>
          <Typography variant="body2" paragraph>
            Un email a été envoyé à <strong>{authUser.email}</strong>.
          </Typography>
          <Button 
            variant="contained" 
            onClick={handleResendEmail}
            sx={{ mt: 2 }}
          >
            Renvoyer l'email de vérification
          </Button>
          <Box mt={2}>
             <Button variant="text" onClick={() => authService.logout().then(() => navigate('/login'))}>
               Se déconnecter
             </Button>
          </Box>
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" className="dashboard-loading">
        <Typography>Chargement...</Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" className="dashboard-error">
        <Typography color="error">Erreur: {error}</Typography>
      </Container>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Tableau de bord Professionnel - Mission Sport</title>
        <meta name="description" content="Gérez vos missions, candidatures et revenus sur votre tableau de bord professionnel Mission Sport. Trouvez de nouvelles opportunités dans le sport." />
        <meta name="keywords" content="tableau de bord, professionnel sport, missions sportives, candidatures, revenus" />
      </Helmet>
      
      <Container maxWidth="lg" className="dashboard-container">
        {/* Header */}
        <Paper className="dashboard-header" elevation={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Box display="flex" alignItems="center">
              <Avatar 
                src={user.avatar} 
                sx={{ width: 64, height: 64, mr: 2 }}
                className="user-avatar"
              />
              <Box>
                <Typography variant="h4" component="h1" className="welcome-title">
                  Bonjour, {user.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {user.profession} • {user.location}
                </Typography>

                <Box display="flex" alignItems="center" mt={1} flexWrap="wrap" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    ⭐ {user.rating.toFixed(1)}/5
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • {user.completedMissions} missions terminées
                  </Typography>
                  {user.experience > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      • {user.experience} ans d'expérience
                    </Typography>
                  )}
                  {user.hourlyRate > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      • {user.hourlyRate}€/h
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Button 
              variant="outlined" 
              startIcon={<FaEdit />}
              onClick={() => navigate('/modifier-profil')}
              className="edit-profile-btn"
            >
              Modifier mon profil
            </Button>
          </Box>
        </Paper>
        
        {/* Statistiques */}
        <Grid container spacing={3} className="stats-section">
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              icon={FaClipboardList}
              title="Missions postulées"
              value={stats.missionsApplied}
              color="#ff9800"
              onClick={() => {
                setMissionTab(1);
                dispatch(setMissionFilter('applied'));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              icon={FaClock}
              title="Missions en cours"
              value={stats.missionsInProgress}
              color="#2196f3"
              onClick={() => {
                setMissionTab(2);
                dispatch(setMissionFilter('in_progress'));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              icon={FaCheckCircle}
              title="Missions terminées"
              value={stats.missionsCompleted}
              color="#4caf50"
              onClick={() => {
                setMissionTab(3);
                dispatch(setMissionFilter('completed'));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              icon={FaEuroSign}
              title="Revenus estimés"
              value={`${stats.estimatedRevenue}€`}
              color="#4caf50"
            />
          </Grid>
        </Grid>
        
        {/* Contenu principal */}
        <Grid container spacing={3} className="main-content">
          {/* Mes missions */}
          <Grid item xs={12} lg={8}>
            <Paper className="missions-section" elevation={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5" component="h2">
                  Mes missions
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<FaSearch />}
                  onClick={() => navigate('/missions')}
                  size="small"
                >
                  Rechercher missions
                </Button>
              </Box>
              
              <Tabs value={missionTab} onChange={handleMissionFilterChange} className="mission-tabs">
                <Tab label="Toutes" />
                <Tab label="Postulées" />
                <Tab label="En cours" />
                <Tab label="Terminées" />
              </Tabs>
              
              <Box className="missions-list">
                {filteredMissions.length > 0 ? (
                  <Grid container spacing={2}>
                    {filteredMissions.slice(0, 6).map((mission) => (
                      <Grid item xs={12} md={6} key={mission.id}>
                        <MissionCard mission={mission} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box className="empty-state" textAlign="center" py={4}>
                    <FaClipboardList size={48} color="#ccc" />
                    <Typography variant="h6" color="text.secondary" mt={2}>
                      Aucune mission trouvée
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Vous n'avez pas encore de missions dans cette catégorie.
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<FaSearch />}
                      onClick={() => navigate('/missions')}
                    >
                      Rechercher des missions
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={3}>
              {/* Notifications */}
              <Grid item xs={12}>
                <Paper className="notifications-section" elevation={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" component="h3">
                      Notifications
                    </Typography>
                    <Badge badgeContent={unreadNotifications} color="error">
                      <FaBell />
                    </Badge>
                  </Box>
                  
                  {recentNotifications.length > 0 ? (
                    <NotificationList 
                      notifications={recentNotifications}
                      onMarkAsRead={handleNotificationRead}
                    />
                  ) : (
                    <Box className="empty-state" textAlign="center" py={2}>
                      <FaBell size={32} color="#ccc" />
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Aucune notification
                      </Typography>
                    </Box>
                  )}
                  
                  <Button 
                    fullWidth 
                    variant="text" 
                    size="small"
                    onClick={() => navigate('/notifications')}
                  >
                    Voir toutes les notifications
                  </Button>
                </Paper>
              </Grid>
              
              {/* Messages récents */}
              <Grid item xs={12}>
                <Paper className="messages-section" elevation={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" component="h3">
                      Messages récents
                    </Typography>
                    <Badge badgeContent={unreadMessages} color="error">
                      <FaEnvelope />
                    </Badge>
                  </Box>
                  
                  {recentMessages.length > 0 ? (
                    <MessagePreview 
                      messages={recentMessages}
                      onMarkAsRead={handleMessageRead}
                    />
                  ) : (
                    <Box className="empty-state" textAlign="center" py={2}>
                      <FaEnvelope size={32} color="#ccc" />
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Aucun message
                      </Typography>
                    </Box>
                  )}
                  
                  <Button 
                    fullWidth 
                    variant="contained" 
                    startIcon={<FaEnvelope />}
                    onClick={() => navigate('/messages')}
                  >
                    Accéder à la messagerie
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Revenus */}
        <Grid container spacing={3} className="revenue-section">
          <Grid item xs={12} md={8}>
            <Paper className="revenue-chart-section" elevation={1}>
              <RevenueChart revenueData={revenue} />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper className="cta-section" elevation={1}>
              <Typography variant="h6" component="h3" gutterBottom>
                Actions rapides
              </Typography>
              
              <Box className="cta-buttons">
                <Button 
                  fullWidth 
                  variant="contained" 
                  startIcon={<FaSearch />}
                  onClick={() => navigate('/missions')}
                  className="cta-primary"
                  size="large"
                >
                  Rechercher de nouvelles missions
                </Button>
                
                <Button 
                  fullWidth 
                  variant="outlined" 
                  startIcon={<FaUser />}
                  onClick={() => navigate('/modifier-profil')}
                  className="cta-secondary"
                  size="large"
                >
                  Compléter mon profil
                </Button>
                
                <Button 
                  fullWidth 
                  variant="text" 
                  startIcon={<FaChartLine />}
                  onClick={() => navigate('/analytics')}
                  size="large"
                >
                  Voir mes statistiques
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default DashboardPro;